import { useState, type FormEvent } from 'react';

import { Alert, Button, Code, CopyButton, Group, Stack, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/shared/api/http';
import { MFA_ERROR_CODES, type MfaEnrollment, type MfaRecoveryCodes } from '@/shared/api/mfa';
import { QrCode } from '@/shared/components/QrCode';

import { normalizeTotp } from './totpCode';

/**
 * The two steps of a new TOTP factor (08 §2.10): scan the secret (or type it) and prove it with the
 * first code. Shared by the enrolment a policy demands at login and the account's security page;
 * the caller says how to confirm and what an expired challenge means for it.
 */
export function TotpEnrollment({
  enrollment,
  confirm,
  onConfirmed,
  onExpired,
}: {
  enrollment: MfaEnrollment;
  confirm: (code: string) => Promise<MfaRecoveryCodes>;
  onConfirmed: (result: MfaRecoveryCodes) => void;
  onExpired?: () => void;
}) {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = normalizeTotp(code);
    if (!/^\d{6}$/.test(value)) {
      setErrorKey('mfa.error.format');
      return;
    }
    setErrorKey(null);
    setPending(true);
    try {
      onConfirmed(await confirm(value));
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.errorCode === MFA_ERROR_CODES.invalidChallenge &&
        onExpired
      ) {
        onExpired();
        return;
      }
      setErrorKey(
        error instanceof ApiError && error.errorCode === MFA_ERROR_CODES.invalidCode
          ? 'mfa.error.enrollCode'
          : 'mfa.error.generic',
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        <Text size="sm">{t('mfa.enroll.scan')}</Text>
        <Group justify="center">
          <QrCode value={enrollment.otpauthUri} label={t('mfa.enroll.qrLabel')} />
        </Group>
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            {t('mfa.enroll.manual')}
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Code style={{ wordBreak: 'break-all' }}>
              {enrollment.secret.replace(/(.{4})/g, '$1 ').trim()}
            </Code>
            <CopyButton value={enrollment.secret}>
              {({ copied, copy }) => (
                <Button variant="subtle" size="compact-xs" onClick={copy}>
                  {copied ? t('mfa.codes.copied') : t('mfa.codes.copy')}
                </Button>
              )}
            </CopyButton>
          </Group>
        </Stack>
        {errorKey !== null ? (
          <Alert color="red" variant="light" role="alert">
            {t(errorKey)}
          </Alert>
        ) : null}
        <TextInput
          label={t('mfa.code')}
          description={t('mfa.enroll.codeHint')}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          value={code}
          onChange={(event) => setCode(event.currentTarget.value)}
        />
        <Button type="submit" loading={pending}>
          {t('mfa.enroll.confirm')}
        </Button>
      </Stack>
    </form>
  );
}
